import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ReactTable from "../../components/common/ReactTable";
import SadhuSadhviVisitService from "../../Service/SadhuSadhviVisitService";

const getLocationName = (value, keys) => {
  if (!value) return "—";
  if (typeof value === "string") return value;
  return keys.map((key) => value?.[key]).find(Boolean) || "—";
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const SadhuSadhviVisitList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    SadhuSadhviVisitService.getMySadhuSadhviVisits()
      .then((data) => {
        if (active) setRows(data);
      })
      .catch((requestError) => {
        if (active) {
          setError(
            requestError?.response?.data?.message ||
              "Unable to load submitted forms.",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
  
    return () => {
      active = false;
    };
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "sadhuName",
        headerName: "Sadhu/Sadhvi Name",
        minWidth: 180,
      },
      {
        field: "contactPerson",
        headerName: "Contact Person",
        minWidth: 170,
      },
      {
        field: "contactNumber",
        headerName: "Mobile Number",
        minWidth: 140,
      },
      {
        field: "location",
        headerName: "Location",
        minWidth: 240,
        searchKey: (row) =>
          [
            getLocationName(row.village, ["villageName", "name"]),
            getLocationName(row.taluka, ["talukaName", "name"]),
            getLocationName(row.district, ["districtName", "name"]),
            getLocationName(row.state, ["stateName", "StateName", "name"]),
          ].join(" "),
        renderCell: (row) =>
          [
            getLocationName(row.village, ["villageName", "name"]),
            getLocationName(row.taluka, ["talukaName", "name"]),
            getLocationName(row.district, ["districtName", "name"]),
            getLocationName(row.state, ["stateName", "StateName", "name"]),
          ].join(", "),
      },
      // {
      //   field: "createdAt",
      //   headerName: "Submitted On",
      //   minWidth: 180,
      //   renderCell: (row) => formatDate(row.createdAt),
      // },
      {
        field: "action",
        headerName: "Action",
        disableSort: true,
        disableSearch: true,
        minWidth: 100,
        renderCell: (row) => (
          <Tooltip title="View submitted form">
            <IconButton
              color="primary"
              onClick={() => navigate(`./${row._id}`)}
              aria-label={`View ${row.sadhuName || "submitted form"}`}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate],
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate("../sadhuSadhviVisitForm")}
          sx={{alignSelf:"flex-start"}}
        >
          Back
        </Button>
        <Typography variant="h3" fontWeight={600} sx={{textAlign:"center"}}>
          Submitted Sadhu Sadhvi Visit Forms
        </Typography>

        <Box>
          {" "}
        </Box>
      </Box>

      <ReactTable
        rowKey="_id"
        columns={columns}
        rows={rows}
        loading={loading}
        initialOrderBy="createdAt"
        initialOrder="desc"
        initialRowsPerPage={10}
      />

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SadhuSadhviVisitList;
